/**
 * PatientRecetasTab — las recetas del paciente, DESDE SU FICHA.
 *
 * Antes solo se podían emitir desde Quirófano (2026-08-19: «que le
 * puedas sacar las recetas a un paciente desde su ficha, que no te tengas que
 * meter en quirófano»). Mismo circuito que allí: el botiquín de medicamentos
 * de /api/recetas/medications, POST /api/recetas, y abrir el PDF.
 *
 * OJO: abrir el PDF de una receta en borrador CONSUME un número CVE del
 * colegio y la marca como emitida — el aviso está en el botón.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pill, Plus, Printer } from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { recipePreviewArgs } from '../../lib/patientDocsHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ESTADO = {
  draft: { label: 'Borrador', clase: 'bg-amber-100 text-amber-800' },
  printed: { label: 'Emitida', clase: 'bg-green-100 text-green-800' },
};

export default function PatientRecetasTab({ patient, onPreview }) {
  const [recetas, setRecetas] = useState(null);
  const [meds, setMeds] = useState([]);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [creando, setCreando] = useState(null);
  // Selección múltiple (2026-08-27): marcar varias recetas y
  // mandarlas a imprimir todas de golpe en un solo PDF.
  const [seleccion, setSeleccion] = useState(() => new Set());
  const alternar = (id) => setSeleccion((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const cargar = useCallback(() => {
    api.get('/api/recetas', { params: { patient_id: patient.id } })
      .then((r) => setRecetas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecetas([]));
  }, [patient.id]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/api/recetas/medications')
      .then((r) => setMeds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMeds([]));
  }, []);

  const patientName = `${patient.name || ''} ${patient.surname1 || ''}`.trim();

  const crear = async (med) => {
    if (creando) return;
    setCreando(med.id);
    try {
      const res = await api.post('/api/recetas', {
        patient_id: patient.id,
        medication_id: med.id, medication_name: med.name,
        presentation: med.presentation, dose: med.dose, route: med.route,
        posology: med.posology || '', duration: med.duration || '',
        packages: med.packages || 1,
      });
      const falta = res.data?.ficha_incompleta;
      if (falta?.length) {
        toast.warning(`Falta ${falta.join(' y ')} en la ficha — la receta saldrá incompleta.`, { duration: 7000 });
      }
      toast.success(`Receta creada: ${med.name}`);
      setNuevaOpen(false);
      cargar();
      onPreview?.(recipePreviewArgs({
        recipeId: res.data.id, medicationName: res.data.medication_name, patientName,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la receta');
    } finally {
      setCreando(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Mismo circuito que en Quirófano: al <strong>imprimir</strong> una receta en borrador
          se emite con su número CVE del colegio.
        </p>
        <Button size="sm" onClick={() => setNuevaOpen((v) => !v)}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="receta-nueva">
          <Plus className="w-4 h-4 mr-1" /> Nueva receta
        </Button>
      </div>

      {nuevaOpen && (
        <Card className="cp-card p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Elige el medicamento (el botiquín se gestiona en Recetas)
          </div>
          {meds.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay medicamentos en el botiquín.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {meds.map((m) => (
                <button key={m.id} type="button" onClick={() => crear(m)} disabled={!!creando}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9E3DD] px-3 py-1.5 text-sm hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.05)] disabled:opacity-50"
                  data-testid={`receta-med-${m.id}`}>
                  {creando === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pill className="w-3.5 h-3.5 text-[#2E6E5E]" />}
                  <span className="font-medium">{m.name}</span>
                  {m.dose ? <span className="text-muted-foreground">{m.dose}</span> : null}
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {seleccion.size > 0 && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="recetas-imprimir-lote"
            title="Las que estén en borrador se emiten (consumen su CVE) y salen todas en un único PDF"
            onClick={() => {
              const ids = recetas.filter((r) => seleccion.has(r.id)).map((r) => r.id);
              onPreview?.({
                open: true,
                url: `${BACKEND_URL}/api/recetas/lote/pdf?ids=${ids.join(',')}`,
                filename: `recetas_${patientName.replace(/\s+/g, '_')}.pdf`,
                title: `${ids.length} recetas · ${patientName}`,
              });
              setSeleccion(new Set());
              setTimeout(cargar, 1200);
            }}
          >
            <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir seleccionadas ({seleccion.size})
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>Quitar selección</Button>
        </div>
      )}

      {recetas === null ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando recetas…
        </div>
      ) : recetas.length === 0 ? (
        <Card className="cp-card p-6 text-center">
          <Pill className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Sin recetas. Crea la primera con «Nueva receta».</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {recetas.map((r) => {
            const est = ESTADO[r.status] || { label: r.status || '—', clase: 'bg-muted text-foreground' };
            return (
              <Card key={r.id} className="cp-card p-3 flex items-center gap-3" data-testid={`receta-fila-${r.id}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[#2E6E5E] shrink-0 cursor-pointer"
                  checked={seleccion.has(r.id)}
                  onChange={() => alternar(r.id)}
                  title="Marcar para imprimir en lote"
                  data-testid={`receta-check-${r.id}`}
                />
                <Pill className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.medication_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(r.created_at)}{r.cve ? ` · CVE ${r.cve}` : ''}
                  </div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${est.clase}`}>{est.label}</span>
                <Button size="sm" variant="outline"
                  title={r.status === 'draft' ? 'Al imprimir se emite y consume un CVE' : 'Ver / reimprimir copia'}
                  onClick={() => onPreview?.(recipePreviewArgs({
                    recipeId: r.id, medicationName: r.medication_name, patientName,
                  }))}
                  data-testid={`receta-pdf-${r.id}`}>
                  <Printer className="w-3.5 h-3.5 mr-1" />
                  {r.status === 'draft' ? 'Emitir e imprimir' : 'PDF'}
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
